import { useState, useEffect, useCallback } from 'react';
import { Heart, Zap, Pencil } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { analytics } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECS_MIN = 60;
const SECS_HOUR = 3600;
const SECS_DAY = 86400;

function timeAgo(d) {
  const s = Math.floor((new Date() - new Date(d)) / 1000);
  if (s < SECS_MIN) return 'just now';
  if (s < SECS_HOUR) return `${Math.floor(s / SECS_MIN)}m ago`;
  if (s < SECS_DAY) return `${Math.floor(s / SECS_HOUR)}h ago`;
  return `${Math.floor(s / SECS_DAY)}d ago`;
}

const ReplyCard = ({ vent, sessionId }) => {
  const [hearts, setHearts] = useState(vent.hearts);
  const [lightnings, setLightnings] = useState(vent.lightnings);
  const [reacted, setReacted] = useState({ has_hearted: false, has_lightninged: false });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(vent.content);
  const [content, setContent] = useState(vent.content);

  const isOwner = vent.session_id === sessionId;

  useEffect(() => {
    axios.get(`${API}/vents/${vent.id}/user-reactions?session_id=${sessionId}`)
      .then(r => setReacted(r.data))
      .catch(() => {});
  }, [vent.id, sessionId]);

  const react = async (type) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/vents/react`, { vent_id: vent.id, reaction_type: type, session_id: sessionId });
      const added = r.data.action === 'added';
      if (type === 'heart') { setHearts(p => added ? p+1 : p-1); setReacted(p => ({...p, has_hearted: added})); }
      else { setLightnings(p => added ? p+1 : p-1); setReacted(p => ({...p, has_lightninged: added})); }
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    try {
      const r = await axios.put(`${API}/vents/${vent.id}`, { content: editText.trim(), session_id: sessionId });
      setContent(r.data.content);
      setEditing(false);
      toast.success('Updated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="glass/50 ml-6 sm:ml-8 border-l-2 border-vent-primary/20 rounded-xl p-3" data-testid={`reply-card-${vent.id}`}>
      {editing ? (
        <div className="mb-2">
          <textarea value={editText} onChange={e => setEditText(e.target.value)} className="w-full bg-black/40 border border-vent-primary/30 rounded-lg p-2 text-white text-sm resize-none" rows={2} />
          <div className="flex gap-2 mt-1">
            <button onClick={saveEdit} className="px-2 py-1 bg-vent-primary text-white text-xs rounded-lg">Save</button>
            <button onClick={() => { setEditing(false); setEditText(content); }} className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded-lg">Cancel</button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-zinc-300 mb-2">{content}</p>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-zinc-600 text-xs">{timeAgo(vent.created_at)}</span>
          {isOwner && !editing && <button onClick={() => setEditing(true)} className="text-zinc-600 hover:text-zinc-400"><Pencil className="w-3 h-3" /></button>}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => react('heart')} className={`flex items-center gap-1 text-xs ${reacted.has_hearted ? 'text-red-500' : 'text-zinc-500'}`}>
            <Heart className="w-3 h-3" fill={reacted.has_hearted ? 'currentColor' : 'none'} />{hearts}
          </button>
          <button onClick={() => react('lightning')} className={`flex items-center gap-1 text-xs ${reacted.has_lightninged ? 'text-yellow-500' : 'text-zinc-500'}`}>
            <Zap className="w-3 h-3" fill={reacted.has_lightninged ? 'currentColor' : 'none'} />{lightnings}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReplyCard;
